import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

interface SessionContextType {
  showSessionExpiredModal: boolean;
  setShowSessionExpiredModal: (show: boolean) => void;
  triggerSessionExpired: () => void;
  sessionEmployerId: string | null;
  setSessionEmployerId: (id: string | null) => void;
  isLoggedIn: boolean;
}

const SESSION_EMPLOYER_ID_KEY = 'session_employer_id';
const SESSION_TOKEN_KEY = 'session_token';

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

interface SessionProviderProps {
  children: ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [sessionEmployerId, setSessionEmployerIdState] = useState<string | null>(() => {
    return localStorage.getItem(SESSION_EMPLOYER_ID_KEY);
  });

  // Setter that syncs with localStorage
  const setSessionEmployerId = (id: string | null) => {
    setSessionEmployerIdState(id);
    if (id) {
      localStorage.setItem(SESSION_EMPLOYER_ID_KEY, id);
    } else {
      localStorage.removeItem(SESSION_EMPLOYER_ID_KEY);
    }
  };

  // Clear sessionEmployerId when session_token is cleared
  useEffect(() => {
    const handleStorage = () => {
      if (!localStorage.getItem(SESSION_TOKEN_KEY)) {
        setSessionEmployerId(null);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const triggerSessionExpired = () => {
    setShowSessionExpiredModal(true);
    setSessionEmployerId(null);
  };

  const value: SessionContextType = {
    showSessionExpiredModal,
    setShowSessionExpiredModal,
    triggerSessionExpired,
    sessionEmployerId,
    setSessionEmployerId,
    isLoggedIn: !!localStorage.getItem(SESSION_TOKEN_KEY)
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}; 